import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthState, CurrentUser } from '@/types';

/**
 * Who is signed in.
 *
 * Redux holds this rather than React Query because it is client state, not
 * server state: it is read synchronously by route guards and by the header on
 * every render, and it has no cache lifetime.
 *
 * The access token is mirrored here for rendering decisions only. The copy the
 * network layer uses lives in services/api/tokens.ts, because the client must
 * not import the store.
 */
const initialState: AuthState = {
  user: null,
  accessToken: null,
  /*
   * Starts true. On boot there may be a stored refresh token, and a guard that
   * ran before that settled would bounce a signed-in user to the login screen
   * for a frame. Guards wait for this to become false.
   */
  isRestoring: true,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    sessionStarted(state, action: PayloadAction<{ user: CurrentUser; accessToken: string }>) {
      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
      state.isRestoring = false;
    },
    userLoaded(state, action: PayloadAction<CurrentUser>) {
      state.user = action.payload;
    },
    /** Signed out, or the session was revoked underneath us. */
    sessionEnded(state) {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.isRestoring = false;
    },
    restoreFinished(state) {
      state.isRestoring = false;
    },
  },
});

export const { sessionStarted, userLoaded, sessionEnded, restoreFinished } = authSlice.actions;
export const authReducer = authSlice.reducer;
